import React from "react";

export type AuthOptionItem = {
  key: string;
  label: string;
  icon?: React.ReactNode;
};

type CommonProps = {
  label: string;
  options: AuthOptionItem[];
  paddingClass?: string;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: AuthOptionItem | null;
  onChange: (opt: AuthOptionItem) => void;
};

type MultiProps = CommonProps & {
  multiple: true;
  value: AuthOptionItem[];
  onChange: (opt: AuthOptionItem[]) => void;
};

type AuthOptionGroupProps = SingleProps | MultiProps;

const AuthOptionGroup: React.FC<AuthOptionGroupProps> = (props) => {
  const { label, options, paddingClass } = props;
  const multiple = props.multiple === true;

  const isSelected = (option: AuthOptionItem) => {
    if (multiple) {
      return props.value.some((v) => v.key === option.key);
    }
    return props.value?.key === option.key;
  };

  const handleSelect = (option: AuthOptionItem) => {
    if (!multiple) {
      // ✅ here TS knows props is SingleProps
      props.onChange(option);
      return;
    }

    // ✅ here TS knows props is MultiProps
    const value = props.value;
    const exists = value.some((v) => v.key === option.key);

    props.onChange(
      exists ? value.filter((v) => v.key !== option.key) : [...value, option],
    );
  };

  return (
    <div className="space-y-2">
      <label className="text-[#121217] text-sm font-medium tracking-normal whitespace-nowrap">
        {label}
      </label>

      <div className="flex items-center flex-wrap gap-2">
        {options.map((option) => {
          const active = isSelected(option);

          return (
            <div
              key={option.key}
              onClick={() => handleSelect(option)}
              className={`
                flex items-center gap-x-1
                rounded-lg
                border
                cursor-pointer
                transition
                h-[42px]
                ${paddingClass ?? "py-2 px-[7px]"}
                ${active ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}
              `}
            >
              {option.icon}
              <p className="text-[#000000] text-xs tracking-[-0.5px]">
                {option.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AuthOptionGroup;
